import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Form,
	Input,
	Result,
	Row,
	Select,
	Space,
	Table,
} from 'antd';
import {
	ArrowRightOutlined,
	FileExcelOutlined,
	CheckCircleOutlined,
} from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useApp } from '@/App';
import FleetModal from '@/libs/antd/components/Modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	getFRMVisitReportList,
	requestFRMSiteReportVerification,
} from '@/libs/fleet-api/services/visit-reports';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import dayjs from 'dayjs';
import { FleetPagination, FleetQueryParams } from '@/libs/fleet-api/fetcher';
import GenericErrorMessage from '@/components/GenericErrorMessage';

const FRMVisitReportListPage: FC = () => {
	const { navigate, notification } = useApp();
	const { openCustomerDetail } = useModals();
	const { form } = useForm<{ notes: string }>();

	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});
	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
	});

	const handleSearch = () => {
		setQueryParams((current) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			} else if (searchBy === 'status') {
				return {
					...current,
					search: undefined,
					reviewStatus: parseInt(searchValue),
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
			};
		});
	};

	const { data, isFetching, refetch, isError } = useQuery({
		queryKey: ['getFRMVisitReportList', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getFRMVisitReportList(queryParams);
		},
	});

	const dataSource =
		data?.data.items.map((item, key) => {
			return { ...item, key };
		}) ?? [];

	const [showModal, setShowModal] = useState<number | null>(null);
	const requestFRMSiteReportVerificationMutation = useMutation({
		mutationFn: requestFRMSiteReportVerification,
	});

	if (isError) return <GenericErrorMessage />;

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<FleetTitle>Input Laporan Kunjungan</FleetTitle>
			<Form
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={handleSearch}
			>
				<Row gutter={[20, 20]}>
					<Col flex="auto">
						<Form.Item
							label={<span>Search By</span>}
							labelCol={{ flex: 'auto' }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								placeholder="Search By"
								options={[
									{
										label: 'Customer Name',
										value: 'customer-name',
									},
									{
										label: 'NPWP',
										value: 'npwp',
									},
									{
										label: 'Status',
										value: 'status',
									},
								]}
								style={{
									display: 'block',
									width: '100%',
								}}
								onChange={(value) => {
									setSearchValue(null);
									setSearchBy(value);
								}}
							/>
						</Form.Item>
					</Col>
					<Col style={{ width: 300 }}>
						<Form.Item>
							{searchBy === 'status' ? (
								<Select
									options={[
										{ label: 'New', value: '1' },
										{ label: 'Return', value: '2' },
										{ label: 'Reject', value: '3' },
									]}
									value={searchValue}
									onChange={(value) => {
										setSearchValue(value);
									}}
									placeholder="Select Status"
								/>
							) : (
								<Input
									placeholder="Search..."
									style={{
										display: 'block',
									}}
									disabled={searchBy === null}
									value={searchValue!}
									onChange={(e) => {
										setSearchValue(e.target.value);
									}}
								/>
							)}
						</Form.Item>
					</Col>
					<Col>
						<Form.Item>
							<Button
								block
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				dataSource={dataSource}
				loading={isFetching}
				pagination={{
					total: data?.data.totalCount,
					current: pagination.pageNumber,
					pageSize: pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setPagination((current) => {
							return {
								pageSize,
								pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
							};
						});
						setQueryParams((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination?.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
				columns={[
					{
						width: 1,
						title: 'No',
						align: 'center',
						dataIndex: ['no'],
					},
					{
						title: 'Prospect ID',
						dataIndex: ['prospectId'],
					},
					{
						title: 'Nama Customer',
						render: (_, record) => (
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(record.customerId);
								}}
							>
								{record.customerName}
							</a>
						),
					},
					{
						title: 'Date of Input',
						render: (_, record) =>
							dayjs(record.prospectCreatedAt).format('DD/MM/YYYY'),
					},
					{
						title: 'Group Customer BMPP',
						dataIndex: ['customerGroupName'],
						render: (_, record) => record.customerGroupName ?? '-',
					},
					{
						title: 'Department',
						dataIndex: ['department'],
					},
					{
						title: 'Fleet RM',
						dataIndex: ['frmName'],
					},
					{
						title: 'Sektor Industri',
						render: (_, record) => record.industryClass ?? '-',
					},
					{
						title: 'Status',
						align: 'center',
						render: (_, record) => {
							return (
								<span
									className={`review-status ${record.reviewStatus.toLowerCase()}`}
								>
									{record.reviewStatus.toLowerCase()}
								</span>
							);
						},
					},
					{
						title: 'LK Kantor',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									style={
										record.isOfficeObservationCompleted
											? { color: 'green' }
											: {}
									}
									onClick={(e) => {
										e.preventDefault();
										navigate('FRMVisitReportPage', {
											prospectId: record.prospectId,
										});
									}}
								>
									{record.isOfficeObservationCompleted ? (
										<CheckCircleOutlined />
									) : (
										<ArrowRightOutlined />
									)}
								</a>
							);
						},
					},
					{
						title: 'LK Site',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									style={record.isSiteReportCompleted ? { color: 'green' } : {}}
									onClick={(e) => {
										e.preventDefault();
										navigate('FRMSiteVisitReportPage', {
											prospectId: record.prospectId,
										});
									}}
								>
									{record.isSiteReportCompleted ? (
										<CheckCircleOutlined />
									) : (
										<ArrowRightOutlined />
									)}
								</a>
							);
						},
					},
					{
						title: 'Call Report',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									style={record.isCallReportCompleted ? { color: 'green' } : {}}
									onClick={(e) => {
										e.preventDefault();
										navigate('FRMCallReportPage', {
											prospectId: record.prospectId,
										});
									}}
								>
									{record.isCallReportCompleted ? (
										<CheckCircleOutlined />
									) : (
										<ArrowRightOutlined />
									)}
								</a>
							);
						},
					},
					{
						title: 'Reject',
						align: 'center',
						render: (_, record) => {
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										navigate('FRMVisitReportRejectPage', {
											prospectId: record.prospectId,
										});
									}}
									style={record.isCallReportCompleted ? { color: 'green' } : {}}
								>
									<FileExcelOutlined />
								</a>
							);
						},
					},
					{
						title: 'Request Approval',
						align: 'center',
						width: 1,
						render: (_, record) => {
							if (
								!record.isSiteReportCompleted ||
								!record.isOfficeObservationCompleted
							) {
								return <ArrowRightOutlined style={{ color: '#bababa' }} />;
							}
							return (
								<a
									onClick={(e) => {
										e.preventDefault();
										setShowModal(record.surveyId);
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
			/>

			<FleetModal
				open={Boolean(showModal)}
				styles={{ footer: { padding: 0 } }}
				okText="NEXT"
				cancelText="CANCEL"
				afterOpenChange={() => form.resetFields()}
				onCancel={() => setShowModal(null)}
				onOk={() => form.submit()}
				okButtonProps={{
					loading: requestFRMSiteReportVerificationMutation.isPending,
				}}
			>
				<Result
					subTitle={
						<Fragment>
							<Form
								{...ANTD_DEFAULT_PROPS.FORM}
								form={form}
								onFinish={async (values) => {
									try {
										await requestFRMSiteReportVerificationMutation.mutateAsync({
											notes: values.notes,
											surveyId: showModal!,
										});
										notification.success({
											message: 'Success',
											description: 'Request approval success',
										});
									} catch {
										notification.error({
											message: 'Failed',
											description: 'Request approval failed',
										});
									}
									refetch();
									setShowModal(null);
								}}
							>
								<p style={{ color: '#2F2F2F' }}>
									Apakah Anda yakin ingin <b>REQUEST APPROVAL</b> laporan
									kunjungan ini?
								</p>
								<Form.Item
									style={{ textAlign: 'left' }}
									label="Notes"
									labelCol={{ span: 8 }}
									name="notes"
									rules={[
										{
											required: true,
											message: 'Notes harus diisi',
										},
									]}
								>
									<Input.TextArea rows={3} />
								</Form.Item>
							</Form>
						</Fragment>
					}
				></Result>
			</FleetModal>
		</Space>
	);
};

export default FRMVisitReportListPage;
